'use client'

import Image from 'next/image'
import React from 'react'
import { Button } from '@/frontend/design-system/actions/Button'
import { H1, H2, H3 } from '@/frontend/design-system/typography/Heading'
import { ContentCard } from './Cards/ContentList'
import { DeepDiveCard } from './Cards/DeepDive'
import { FocusCard } from './Cards/Focus'
import { CodeBlock } from './CodeBlock'

const reactComponents: Record<string, React.ElementType> = {
  Button: props => (
    <div className='mdx-data-container'>
      <Button {...props} />
    </div>
  ),
  ContentCard: props => (
    <div className='mdx-data-container'>
      <ContentCard {...props} />
    </div>
  ),
  FocusCard: props => (
    <div className='mdx-data-container'>
      <FocusCard {...props} />
    </div>
  ),
  DeepDive: props => (
    <div className='mdx-data-container'>
      <DeepDiveCard {...props} />
    </div>
  )
}

const markdownStyles = {
  p: 'leading-[24px] whitespace-pre-line font-normal',
  code: 'bg-backgroundTertiary rounded font-mono py-0.5 px-1 wrap-break-word',
  h1: 'header !mt-0 mb-4',
  h2: 'header !mt-0 mb-3',
  h3: 'header !mt-0 mb-2',
  blockquote:
    'border-l-4 border-algosecondary bg-slate-100 dark:bg-slate-700/50 pl-4 italic text-contentSecondary',
  a: 'text-blue-500 underline',
  ul: 'list-disc list-outside pl-6 [&_ul]:ml-1 [&_ul]:list-[circle]',
  ol: 'list-decimal list-outside pl-6 [&_ol]:ml-1 [&_ol]:list-[lower-alpha]',
  strong: 'font-bold',
  em: 'italic',
  hr: 'border-t border-borderPrimary mt-2',

  table: 'w-full border border-borderPrimary overflow-hidden custom-scrollbar',
  th: 'text-center px-4 py-2 font-bold border-b border-r last:border-r-0 border-borderPrimary',
  td: 'px-3 py-2 border-b border-r border-borderPrimary wrap-break-word',
  tr: 'last:border-0 even:bg-backgroundHoverOverlay',
  thead: '',
  tbody: '',

  input:
    'mr-2 align-middle accent-algoprimary pointer-events-none ' + '[&:not(:checked)]:opacity-60'
}

const htmlComponents: Record<string, React.ElementType> = {
  img: (props: React.ComponentProps<typeof Image>) => (
    <Image
      {...props}
      alt={props.alt ?? ''}
      width={props.width ?? 400}
      height={props.height ?? 300}
    />
  ),

  // math styles
  span: (props: React.HTMLAttributes<HTMLSpanElement>) => {
    const className = props.className ?? ''
    if (className.includes('katex')) {
      return <span {...props} className={`${className} px-1 text-contentPrimary`} />
    }
    return <span {...props} />
  },

  // inline code
  code: props => <code className={markdownStyles.code} {...props} />,
  // block code with syntax highlighting
  pre: ({
    children
  }: {
    children: React.ReactElement<{ className?: string; children: string | string[] }>
  }) => {
    const codeChild = React.Children.only(children)
    const codeString = Array.isArray(codeChild.props.children)
      ? codeChild.props.children.join('')
      : (codeChild.props.children ?? '')

    return <CodeBlock className={codeChild.props.className}>{codeString}</CodeBlock>
  },

  p: props => <p className={markdownStyles.p} {...props} />,
  h1: props => <H1 className={markdownStyles.h1} {...props} />,
  h2: props => <H2 className={markdownStyles.h2} {...props} />,
  h3: props => <H3 className={markdownStyles.h3} {...props} />,
  blockquote: props => <blockquote className={markdownStyles.blockquote} {...props} />,
  a: props => <a className={markdownStyles.a} {...props} />,
  ul: props => <ul className={markdownStyles.ul} {...props} />,
  ol: props => <ol className={markdownStyles.ol} {...props} />,
  strong: props => <strong className={markdownStyles.strong} {...props} />,
  em: props => <em className={markdownStyles.em} {...props} />,
  hr: props => <hr className={markdownStyles.hr} {...props} />,

  table: props => (
    <div className='my-4 overflow-x-auto'>
      <table className={markdownStyles.table} {...props} />
    </div>
  ),
  thead: props => <thead className={markdownStyles.thead} {...props} />,
  tbody: props => <tbody className={markdownStyles.tbody} {...props} />,
  tr: props => <tr className={markdownStyles.tr} {...props} />,
  th: props => <th className={markdownStyles.th} {...props} />,
  td: props => <td className={markdownStyles.td} {...props} />,

  input: props => <input {...props} disabled={false} readOnly className={markdownStyles.input} />
}

export const mdxComponents: Record<string, React.ElementType> = {
  ...htmlComponents,
  ...reactComponents
}
